/**
 * Phase F2 — Buyer Portfolio View
 * --------------------------------
 * Shows linked portfolio detail to a buyer (read-only).
 *
 * Endpoints used:
 *   GET /api/public/portfolio/{portfolio_id}      — base details + creator + utilities
 *   GET /api/portfolios/{portfolio_id}/rules      — portfolio type + funding + distribution rules
 *   GET /api/portfolios/{portfolio_id}/funding-progress — funding status snapshot
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Briefcase, ArrowLeft, DollarSign, TrendingUp, Gift, User, Layers, Hash, Package,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const sectionTitle = {
  fontSize: 15, fontWeight: 700, color: theme.colors.text.primary,
  marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
};

function KV({ label, value, testid }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
        {label}
      </div>
      <div data-testid={testid} style={{ fontSize: 14, color: theme.colors.text.primary, fontWeight: 600 }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

function pct(num, denom) {
  const n = Number(num) || 0;
  const d = Number(denom) || 0;
  if (!d) return 0;
  return Math.min(100, Math.round((n / d) * 100));
}

export default function BuyerPortfolioView() {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [rules, setRules] = useState(null);
  const [progress, setProgress] = useState(null);
  const [attachedUtilities, setAttachedUtilities] = useState([]);
  const [loading, setLoading] = useState(true);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [m, r, p, au] = await Promise.all([
          axios.get(`${API}/public/portfolio/${portfolioId}`).catch(() => ({ data: null })),
          axios.get(`${API}/portfolios/${portfolioId}/rules`, auth).catch(() => ({ data: null })),
          axios.get(`${API}/portfolios/${portfolioId}/funding-progress`, auth).catch(() => ({ data: null })),
          // Phase 8B — holder-only endpoint; 403 for non-holders is treated as empty.
          axios.get(`${API}/buyer/portfolios/${portfolioId}/utilities`, auth).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setMeta(m.data);
        setRules(r.data);
        setProgress(p.data);
        setAttachedUtilities(au?.data?.utilities || []);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load portfolio');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (portfolioId) load();
    return () => { cancelled = true; };
  }, [portfolioId, auth]);

  if (loading) {
    return (
      <div style={{ padding: 24, color: theme.colors.text.muted }} data-testid="portfolio-view-loading">
        Loading portfolio…
      </div>
    );
  }

  const portfolio = meta?.portfolio || {};
  const creator = meta?.creator || {};
  const utilities = meta?.utilities || [];
  const pType = rules?.rules?.portfolio_type || 'UTILITY_ONLY';
  const distModel = rules?.distribution_rules?.distribution_model || '—';
  const fundingRules = rules?.funding_rules || {};

  const target = progress?.target_amount || fundingRules.target_amount || 0;
  const raised = progress?.amount_raised || progress?.raised_amount || 0;
  const fundedPct = pct(raised, target);

  return (
    <div data-testid="buyer-portfolio-view-page" style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <button
        data-testid="portfolio-back-btn"
        onClick={() => navigate(-1)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', cursor: 'pointer',
          color: theme.colors.primary, fontWeight: 600, marginBottom: 16, fontSize: 14,
        }}
      >
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header */}
      <div style={{ ...cardStyle, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{
            width: 120, height: 120, borderRadius: theme.radius.md,
            background: theme.colors.bg.tertiary, overflow: 'hidden', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {portfolio.image_url ? (
              <img src={portfolio.image_url} alt={portfolio.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Briefcase size={36} color={theme.colors.text.muted} />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h1 data-testid="portfolio-name" style={{
              fontSize: 24, fontWeight: 800, color: theme.colors.text.primary, margin: '0 0 6px',
            }}>
              {portfolio.name || 'Portfolio'}
            </h1>
            <div style={{ color: theme.colors.text.secondary, marginBottom: 10 }}>
              {portfolio.description || 'No description.'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <KV label="Category" value={portfolio.category} testid="portfolio-category" />
              <KV label="Status" value={portfolio.status} testid="portfolio-status" />
              <KV label="Portfolio Type" value={pType} testid="portfolio-type" />
              <KV label="Total Units" value={portfolio.total_units} testid="portfolio-units" />
              <KV label="Kickback %" value={`${portfolio.kickback_percent ?? 0}%`} testid="portfolio-kickback" />
            </div>
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16,
      }}>
        {/* Funding Status */}
        <div style={cardStyle} data-testid="portfolio-funding-card">
          <div style={sectionTitle}>
            <TrendingUp size={16} color={theme.colors.primary} /> Funding Status
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: theme.colors.text.secondary, marginBottom: 4 }}>
                <span>Raised</span>
                <span style={{ fontWeight: 700, color: theme.colors.text.primary }}>
                  {raised} / {target} {fundingRules.currency || 'USD'}
                </span>
              </div>
              <div style={{ height: 8, background: theme.colors.bg.tertiary, borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${fundedPct}%`, background: theme.colors.status.success }} />
              </div>
              <div data-testid="portfolio-funded-pct" style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 4 }}>
                {fundedPct}% funded
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <KV label="Funding Type" value={fundingRules.funding_type} testid="portfolio-funding-type" />
              <KV label="Funding Source" value={fundingRules.funding_source} testid="portfolio-funding-source" />
              <KV label="Threshold" value={fundingRules.threshold_amount} testid="portfolio-funding-threshold" />
              <KV label="Sellout Policy" value={rules?.rules?.sellout_policy} testid="portfolio-sellout-policy" />
            </div>
          </div>
        </div>

        {/* Distribution Model */}
        <div style={cardStyle} data-testid="portfolio-distribution-card">
          <div style={sectionTitle}>
            <DollarSign size={16} color={theme.colors.primary} /> Distribution Model
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <KV label="Model" value={distModel} testid="portfolio-distribution-model" />
            <KV label="Cadence" value={rules?.distribution_rules?.distribution_cadence} testid="portfolio-distribution-cadence" />
            <KV label="Currency" value={rules?.distribution_rules?.currency} testid="portfolio-distribution-currency" />
            <KV label="Holder %" value={rules?.distribution_rules?.holder_pool_percent} testid="portfolio-holder-pct" />
            <KV label="Creator %" value={rules?.distribution_rules?.creator_pool_percent} testid="portfolio-creator-pct" />
            <KV label="Provider %" value={rules?.distribution_rules?.provider_pool_percent} testid="portfolio-provider-pct" />
          </div>
        </div>
      </div>

      {/* Utilities */}
      <div style={{ ...cardStyle, marginBottom: 16 }} data-testid="portfolio-utilities-card">
        <div style={sectionTitle}>
          <Gift size={16} color={theme.colors.primary} /> Utilities
        </div>
        {utilities.length === 0 ? (
          <div style={{ color: theme.colors.text.muted, fontSize: 14 }}>No utilities attached.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {utilities.map((u, i) => (
              <div
                key={u.id || i}
                data-testid={`portfolio-utility-${i}`}
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '10px 12px', background: theme.colors.bg.secondary,
                  borderRadius: theme.radius.sm,
                }}
              >
                <div style={{ fontWeight: 600, color: theme.colors.text.primary }}>
                  {u.name || u.title || 'Utility'}
                </div>
                <div style={{ fontSize: 13, color: theme.colors.text.secondary }}>
                  {u.kind || u.type || ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phase 8B — Available Utilities (holder-only endpoint) */}
      <div style={{ ...cardStyle, marginBottom: 16 }} data-testid="buyer-available-utilities">
        <div style={sectionTitle}>
          <Package size={16} color={theme.colors.primary} /> Available Utilities
        </div>
        <div style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 12 }}>
          Utilities connected to this Kickback may be available based on the
          creator&apos;s rules, eligibility, and fulfillment requirements.
        </div>
        {attachedUtilities.length === 0 ? (
          <div data-testid="buyer-utilities-empty" style={{ fontSize: 13, color: theme.colors.text.muted }}>
            No utilities are currently attached to this Kickback.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {attachedUtilities.map(u => (
              <div key={u.id} data-testid={`buyer-utility-${u.id}`}
                style={{ padding: 12, background: theme.colors.bg.secondary,
                  borderRadius: theme.radius.sm, display: 'flex', gap: 12,
                  flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 700, color: theme.colors.text.primary,
                    display: 'flex', gap: 8, alignItems: 'center' }}>
                    {u.utility_name}
                    <span style={{ padding: '2px 8px', borderRadius: 999,
                      background: `${theme.colors.primary}1A`, color: theme.colors.primary,
                      fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>
                      {u.utility_type}
                    </span>
                  </div>
                  {u.fulfillment_responsibility && (
                    <div style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 4 }}>
                      Fulfillment: {u.fulfillment_responsibility}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 2 }}>
                    Attached {u.attached_at ? new Date(u.attached_at).toLocaleDateString() : '—'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: 11, color: theme.colors.text.muted,
          fontStyle: 'italic' }}>
          Benefits and utilities vary by Kickback and may be subject to
          eligibility, availability, and fulfillment rules.
        </div>
      </div>

      {/* Creator Profile */}
      <div style={cardStyle} data-testid="portfolio-creator-card">
        <div style={sectionTitle}>
          <User size={16} color={theme.colors.primary} /> Creator Profile
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <KV label="Name" value={creator.name} testid="creator-name" />
          <KV label="Email" value={creator.email} testid="creator-email" />
          <KV label="Contract Address" value={portfolio.contract_address || '—'} testid="creator-contract" />
        </div>
      </div>
    </div>
  );
}
